import { Suspense, lazy, useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { Outlet, useLocation } from 'react-router-dom';
import { SideBar } from './components';
import { useDefaultUserStore, useSidebarOpenStore } from '../../store';
import { Realtime } from 'ably';
import { AblyProvider, ChannelProvider } from 'ably/react';
import { getAblyClient } from '../../constants';
import { Loader } from '../Loader/Loader';
import * as Sentry from '@sentry/react';
import { TomAssistantSessionHost } from '../../modules/aiAssistant/components/TomAssistantSessionHost';
import { PropertyDetailsModalProvider } from '../../modals/PropertyDetails';
import { Role } from '../../types';
import { useIsBelowBreakpoint } from '../../hooks/useResponsiveLayout';
import { heightBelowMobileNav } from '../../constants/layout';

// Admin-only, so it stays out of the shared bundle.
const AiAssistantWidget = lazy(() =>
	import(
		'../../modules/aiAssistant/components/AiAssistantWidget/AiAssistantWidget'
	).then(module => ({ default: module.AiAssistantWidget })),
);

export const DashboardLayout = () => {
	const { isOpen, onToggle } = useSidebarOpenStore();
	const location = useLocation();

	// TEST
	const [socketClient, setSocketClient] = useState<Realtime | null>(null);

	const { user } = useDefaultUserStore();
	// On the single-pane Messenger layout the launcher would cover the composer.
	const isSinglePaneMessenger = useIsBelowBreakpoint('lg');
	const shouldShowAiAssistantWidget =
		user?.roleId === Role.Admin &&
		!location.pathname.startsWith('/ai-assistant') &&
		!/^\/messenger\/direct\/[^/]+/.test(location.pathname) &&
		!(
			isSinglePaneMessenger &&
			/^\/messenger\/community\/[^/]+/.test(location.pathname)
		);

	useEffect(() => {
		if (!user?.userId) {
			setSocketClient(null);
			return;
		}
		const realtimeClient = getAblyClient(user.userId);
		setSocketClient(realtimeClient);

		// `failed` is terminal (no automatic reconnect), so always report it,
		// even for codes that are transient in other states.
		realtimeClient.connection.on('failed', stateChange => {
			Sentry.captureException(
				stateChange.reason ?? new Error('Ably connection failed'),
				{ tags: { 'ably.connection': 'failed' } },
			);
		});

		return () => {
			realtimeClient.close();
		};
	}, [user?.userId]);

	// TEST

	if (!user?.userId || !socketClient) {
		return (
			<Loader
				variant="brand"
				centerHeight="100dvh"
				centerProps={{ w: '100%' }}
			/>
		);
	}

	return (
		<AblyProvider client={socketClient}>
			<PropertyDetailsModalProvider>
				<TomAssistantSessionHost>
					<Box minH="100dvh" background={'background'}>
						<ChannelProvider
							channelName={`unread-count:${user.userId}`}>
							<SideBar
								menuExpanded={isOpen}
								toggleMenu={onToggle}
								userId={user.userId}
							/>
						</ChannelProvider>
						<Box
							ml={{ base: 0, md: isOpen ? '250px' : '120px' }}
							transition=".3s"
							bg="mainBg"
							minH={{
								base: heightBelowMobileNav(),
								md: '100dvh',
							}}>
							<Outlet />
						</Box>
						{shouldShowAiAssistantWidget ? (
							<Suspense fallback={null}>
								<AiAssistantWidget />
							</Suspense>
						) : null}
					</Box>
				</TomAssistantSessionHost>
			</PropertyDetailsModalProvider>
		</AblyProvider>
	);
};
